import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import '../styles/ExportVote.css';
import '../styles/BaseForm.css';
import '../styles/ExportImage.css';
import '../styles/canvas.css';
import fr from '../locales/fr';
import en from '../locales/en';

function ExportImage({ ws }) {
  const [imageSrc, setImageSrc] = useState(null);
  const browserLang = navigator.language || navigator.userLanguage;
  const language = browserLang.startsWith('fr') ? 'fr' : 'en';
  const translations = language === 'fr' ? fr : en;
  const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [confirmed, setConfirmed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [displayScale, setDisplayScale] = useState(1);

  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const CROP_SIZE = 500;

  const navigate = useNavigate();
  const location = useLocation();

  const allowed =
    location.state?.fromMediaSelect ||
    sessionStorage.getItem('canAccessExportImage');

  // 📐 Calculer le displayScale pour adapter l'affichage à l'écran
  const calculateDisplayScale = () => {
    if (!imageSize.width || !imageSize.height) return 1;

    // Taille maximale disponible pour l'affichage avec marges de sécurité
    const maxWidth = window.innerWidth - 80; // Plus de marge (40px de chaque côté)
    const maxHeight = window.innerHeight - 350; // Plus de marge pour les éléments UI

    // Dimensions virtuelles (image scalée + cadre de crop)
    const virtualWidth = Math.max(CROP_SIZE, imageSize.width);
    const virtualHeight = Math.max(CROP_SIZE, imageSize.height);

    // Calculer le ratio pour que tout rentre à l'écran
    const scaleX = maxWidth / virtualWidth;
    const scaleY = maxHeight / virtualHeight;

    // Prendre le plus petit ratio et limiter entre 0.1 et 1
    return Math.max(0.1, Math.min(1, Math.min(scaleX, scaleY)));
  };

  // Recalculer displayScale quand l'image ou la fenêtre change
  useEffect(() => {
    const newDisplayScale = calculateDisplayScale();
    setDisplayScale(newDisplayScale);

    const handleResize = () => {
      setDisplayScale(calculateDisplayScale());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageSize]);

  // 📤 Upload de l'image
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setImageSrc(event.target.result);
          setOriginalSize({ width: img.width, height: img.height });
          setImageSize({ width: img.width, height: img.height });
          setScale(1);

          // Centrer le cadre de crop sur l'image
          const x = Math.max(0, (img.width - CROP_SIZE) / 2);
          const y = Math.max(0, (img.height - CROP_SIZE) / 2);
          setCropPosition({ x, y });
          setConfirmed(false);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // 📏 Appliquer un nouveau scale
  const applyScale = (newScale) => {
    newScale = Math.max(0.1, Math.min(3, newScale));
    setScale(newScale);

    const newWidth = originalSize.width * newScale;
    const newHeight = originalSize.height * newScale;
    setImageSize({ width: newWidth, height: newHeight });

    // Ajuster la position du crop pour qu'il reste dans les limites
    setCropPosition(prev => ({
      x: Math.max(0, Math.min(newWidth - CROP_SIZE, prev.x)),
      y: Math.max(0, Math.min(newHeight - CROP_SIZE, prev.y))
    }));
  };


  // Déplacer le cadre de crop
  const handleMouseDown = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    // Vérifier si on clique dans le cadre de crop (en coordonnées virtuelles)
    if (virtualX >= cropPosition.x && virtualX <= cropPosition.x + CROP_SIZE &&
        virtualY >= cropPosition.y && virtualY <= cropPosition.y + CROP_SIZE) {
      setIsDragging(true);
      setDragStart({ x: virtualX - cropPosition.x, y: virtualY - cropPosition.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    // Calculer nouvelle position
    let newX = virtualX - dragStart.x;
    let newY = virtualY - dragStart.y;

    // Limites: tous les coins du cadre doivent rester dans le conteneur
    const containerWidth = Math.max(CROP_SIZE, imageSize.width);
    const containerHeight = Math.max(CROP_SIZE, imageSize.height);

    // Le cadre ne peut pas sortir des limites
    const minX = 0;
    const maxX = containerWidth - CROP_SIZE;
    const minY = 0;
    const maxY = containerHeight - CROP_SIZE;

    newX = Math.max(minX, Math.min(maxX, newX));
    newY = Math.max(minY, Math.min(maxY, newY));

    setCropPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Support tactile
  const handleTouchStart = (e) => {
    if (!containerRef.current) return;

    // Drag pour déplacer le cadre (1 doigt)
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const screenX = touch.clientX - rect.left;
      const screenY = touch.clientY - rect.top;

      // Convertir coordonnées écran → virtuelles
      const virtualX = screenX / displayScale;
      const virtualY = screenY / displayScale;

      if (virtualX >= cropPosition.x && virtualX <= cropPosition.x + CROP_SIZE &&
          virtualY >= cropPosition.y && virtualY <= cropPosition.y + CROP_SIZE) {
        setIsDragging(true);
        setDragStart({ x: virtualX - cropPosition.x, y: virtualY - cropPosition.y });
        e.preventDefault();
      }
    }
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current) return;

    // Drag (1 doigt)
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const screenX = touch.clientX - rect.left;
      const screenY = touch.clientY - rect.top;

      // Convertir coordonnées écran → virtuelles
      const virtualX = screenX / displayScale;
      const virtualY = screenY / displayScale;

      let newX = virtualX - dragStart.x;
      let newY = virtualY - dragStart.y;

      // Limites: tous les coins du cadre doivent rester dans le conteneur
      const containerWidth = Math.max(CROP_SIZE, imageSize.width);
      const containerHeight = Math.max(CROP_SIZE, imageSize.height);

      // Le cadre ne peut pas sortir des limites
      const minX = 0;
      const maxX = containerWidth - CROP_SIZE;
      const minY = 0;
      const maxY = containerHeight - CROP_SIZE;

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));

      setCropPosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // ✅ Confirmer = cropper la zone sélectionnée
  const handleConfirm = () => {
    if (!imageSrc) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Canvas de 500x500
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;

    const img = new Image();
    img.onload = () => {
      // Si l'image est plus petite que 500x500, ajouter des bordures
      if (imageSize.width < CROP_SIZE || imageSize.height < CROP_SIZE) {
        // Remplir le fond avec un motif stylisé
        const gradient = ctx.createLinearGradient(0, 0, CROP_SIZE, CROP_SIZE);
        gradient.addColorStop(0, '#667eea');
        gradient.addColorStop(1, '#764ba2');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);

        // Centrer l'image
        const offsetX = (CROP_SIZE - imageSize.width) / 2;
        const offsetY = (CROP_SIZE - imageSize.height) / 2;

        // Dessiner l'image scalée au centre
        ctx.drawImage(
          img,
          0, 0, originalSize.width, originalSize.height,
          offsetX, offsetY, imageSize.width, imageSize.height
        );
      } else {
        // Dessiner normalement la zone croppée
        const sourceX = cropPosition.x / scale;
        const sourceY = cropPosition.y / scale;
        const sourceSize = CROP_SIZE / scale;

        ctx.drawImage(
          img,
          sourceX, sourceY, sourceSize, sourceSize,
          0, 0, CROP_SIZE, CROP_SIZE
        );
      }

      const dataURL = canvas.toDataURL('image/jpeg', 0.9);

      // 🔄 Récupérer infos utilisateur
      const unityID = localStorage.getItem('UNITY');
      const pseudo = localStorage.getItem('pseudo');
      const pseudoColor = localStorage.getItem('pseudoColor');

      // 📤 Envoyer l'image via WebSocket
      ws.send(`${unityID}:NPþ${pseudo}þ${pseudoColor}þPICþFALSEþ${dataURL}`);

      setConfirmed(true);
      sessionStorage.setItem('canAccessReady', 'true');
      navigate('/Ready', { state: { fromAuthorized: true } });
    };
    img.src = imageSrc;
  };

  // Gérer les event listeners pour le drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, cropPosition, imageSize]);

  useEffect(() => {
    if (allowed) {
      sessionStorage.removeItem('canAccessExportImage');
    }
  }, [allowed]);

  if (!allowed) {
    return <Navigate to="/mediaSelect" replace />;
  }

  return (
    <div className="boxform">
      <h1 className="title">{translations.exportImage.title}</h1>

      <label className="file-upload-label">
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="file-upload-input"
        />
        <span className="file-upload-button">{translations.exportImage.chooseButton}</span>
      </label>

      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="image-preview-container"
        style={{
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          width: imageSrc ? `${Math.max(CROP_SIZE, imageSize.width) * displayScale}px` : `${CROP_SIZE}px`,
          height: imageSrc ? `${Math.max(CROP_SIZE, imageSize.height) * displayScale}px` : `${CROP_SIZE}px`,
          maxWidth: 'calc(100vw - 80px)',
          maxHeight: 'calc(100vh - 350px)',
          margin: '0 auto',
          background: (imageSrc && (imageSize.width < CROP_SIZE || imageSize.height < CROP_SIZE))
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'repeating-conic-gradient(#e0e0e0 0% 25%, #f5f5f5 0% 50%) 50% / 20px 20px',
        }}
      >
        {imageSrc && (
          <>
            <img
              ref={imageRef}
              src={imageSrc}
              alt="preview"
              style={{
                position: 'absolute',
                top: imageSize.height < CROP_SIZE ? `${((CROP_SIZE - imageSize.height) / 2) * displayScale}px` : 0,
                left: imageSize.width < CROP_SIZE ? `${((CROP_SIZE - imageSize.width) / 2) * displayScale}px` : 0,
                width: `${imageSize.width * displayScale}px`,
                height: `${imageSize.height * displayScale}px`,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
              draggable={false}
            />

            {/* Overlay semi-transparent pour la zone non-croppée + Cadre de crop */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              <defs>
                <mask id="cropMask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={`${(cropPosition.x / Math.max(CROP_SIZE, imageSize.width)) * 100}%`}
                    y={`${(cropPosition.y / Math.max(CROP_SIZE, imageSize.height)) * 100}%`}
                    width={`${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.width)) * 100}%`}
                    height={`${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.height)) * 100}%`}
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0, 0, 0, 0.6)"
                mask="url(#cropMask)"
              />
              {/* Cadre blanc de crop */}
              <rect
                x={`${(cropPosition.x / Math.max(CROP_SIZE, imageSize.width)) * 100}%`}
                y={`${(cropPosition.y / Math.max(CROP_SIZE, imageSize.height)) * 100}%`}
                width={`${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.width)) * 100}%`}
                height={`${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.height)) * 100}%`}
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                style={{
                  filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.8))'
                }}
              />
            </svg>

            {/* Zone cliquable invisible pour le drag */}
            <div
              style={{
                position: 'absolute',
                left: `${(cropPosition.x / Math.max(CROP_SIZE, imageSize.width)) * 100}%`,
                top: `${(cropPosition.y / Math.max(CROP_SIZE, imageSize.height)) * 100}%`,
                width: `${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.width)) * 100}%`,
                height: `${(CROP_SIZE / Math.max(CROP_SIZE, imageSize.height)) * 100}%`,
                pointerEvents: 'auto',
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
            >
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                pointerEvents: 'none',
                opacity: isDragging ? 0.7 : 0.4,
              }}>
                {translations.exportImage.moveFrame}
              </div>
            </div>
          </>
        )}
      </div>

      {imageSrc && !confirmed && (
        <>
          <div className="size-slider-group" style={{ marginTop: '15px', marginBottom: '15px' }}>
            <label className="canvas-label-picker">
              {translations.exportImage.zoomLabel} <span className="size-value">{Math.round(scale * 100)}%</span>
            </label>
            <input
              type="range"
              min="0.1"
              max="3"
              step="0.01"
              value={scale}
              onChange={(e) => applyScale(parseFloat(e.target.value))}
              className="size-slider-input"
            />
          </div>
          <p className="image-instructions">
            {imageSize.width >= CROP_SIZE && imageSize.height >= CROP_SIZE
              ? translations.exportImage.instructions
              : translations.exportImage.instructionsSmall}
          </p>
          <button onClick={handleConfirm} className="submit-button">
            {translations.exportImage.confirm}
          </button>
        </>
      )}

      {confirmed && <p className="success-message">{translations.exportImage.successMessage}</p>}
    </div>
  );
}

export default ExportImage;
